import {
  Box,
  Button,
  Checkbox,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowBigDown,
  ArrowBigUp,
  ArrowDown,
  ArrowUp,
  TrashIcon,
  WrenchIcon,
} from "lucide-react";
import { VariableSizeList as List, ListChildComponentProps } from "react-window";
import { ESMap, Set } from "typescript";
import { useEffect, useState, useRef, MutableRefObject } from "react";
import { ModifyModal } from "../Modals/ModifyModal";
import { DeleteModal } from "../Modals/DeleteModal";
import { b } from "vite/dist/node/types.d-aGj9QkWt";
import DatePicker from "react-datepicker";
import { downloadExcel } from "../Services/ParticipantService";
import { PromptForConfirmationModal } from "../Modals/PromptForConfirmationModal";
import { PromptForInputModal } from "../Modals/PromptForInputModal";

// What differs from the different tables:
// Type: Participant --> T
//
// filteredData: Array<T>
// highlights?: Map<id, Array<HighlightType|null>>
//
// onChange: What endpoint to use for update (if any) NOTE: needs to update filtered AND non-filtered
// modifiableFields: Array<keyof T>
// modifiableFieldsDisplayNames: Array<string>
// onDelete: What endpoint to use for delete (if any) NOTE: needs to update filtered AND non-filtered
// toRowData: (x: T) => Array<string>
// headers: Array<string>
// rowFormat: string
//
// OBS: can allow sorting on all columns to simplify...
//
//
//
// <FastTable
//    data={filteredData}
//    headers:  [.................]
//    toRowData: (x: T) => Array<string|boolean>
//    rowFormat: "............"
//    onChange = (update: T) => {<URL request> --> storeInNonFiltered --> filteredSouldGetUpdated --> return boolean for closing modal}
//    modifiableFields = {...}
//    modifiableFieldsDisplayNames = {...}
//    onDelete = (old: T) => {<URL request> --> update nonFiltered ----> return boolean}
//
//    suggestions = Map<fieldName: keyof T, Array<string> suggestions>   (updated each data change)
//    uniquenes = Map<string, Set<string> alreadyExistingValues>  (updated each data change, used both for edit and display uniqueness)
//

interface DisplayDataRow<T> {
  headers: Array<string>;
  data: Array<string | boolean | number>;
  rawData: T;
  rowFormat: string;
  onChange?: (updated: T) => Promise<boolean>;
  onDelete?: (old: T) => Promise<boolean>;
  modifiableFields?: Array<keyof T>;
  modifiableFieldsDisplayName?: Array<string>;
  suggestions?: ESMap<keyof T, Set<string>>;
  uniqueness?: ESMap<string, Array<string>>;
  setModifyTarget: (target: T) => void;
  setDeleteTarget: (target: T) => void;
  selectedRow?: T;
  setSelectedRow?: (target: T | undefined) => void;
  onRowSelect?: (row: T | undefined) => void;
  onReorder?: (dragged: T, droppedOn: T) => void;
  dragSourceRef?: MutableRefObject<T | null>;
  highlightedRow?: T;
}

interface FastTableProps<T> {
  data: Array<T>;
  headers: Array<string>;
  toRowData: (x: T) => Array<string | boolean | Date | null | number>;
  rowFormat: string;
  height?: number; // height of the table, default 300px
  rowHeight?: number; // height of each row, default 50px
  // change:
  onChange?: (updated: T, old: T) => Promise<boolean>;
  onDelete?: (old: T) => Promise<boolean>;
  fixedFields?: Array<keyof T>;
  modifiableFields?: Array<keyof T>;
  modifiableFieldsDisplayName?: Array<string>;
  fixedFieldsDisplayName?: Array<string>;
  // improved editing and warnings
  suggestions?: ESMap<keyof T, Set<string>>;
  uniqueness?: ESMap<string, Array<string>>;
  forceDisplayToTypeMap?: Map<string, string>;
  downloadFileName?: string;
  width?: any;
  // row selection; undefined means deselect
  onRowSelect?: (row: T | undefined) => void;
  focusedRow?: T;
  // drag-to-reorder rows; swap finishTime or equivalent field in the callback
  onReorder?: (dragged: T, droppedOn: T) => Promise<void>;
  // excel export column widths (optional, in characters)
  columnWidths?: number[];
  // excel page header (optional title at top)
  pageHeader?: string;
  // excel repeat header rows on print (freeze panes)
  repeatHeaderRows?: number;
}

// <FastTable
//    data={filteredData}
//    headers:  [.................]
//    toRowData: (x: T) => Array<string|boolean>
//    rowFormat: "............"

//    onChange = (update: T) => {<URL request> --> storeInNonFiltered --> filteredSouldGetUpdated --> return boolean for closing modal}
//    onDelete = (old: T) => {<URL request> --> update nonFiltered ----> return boolean}
//    modifiableFields = {...}
//    modifiableFieldsDisplayNames = {...}
//
//    suggestions = Map<fieldName: keyof T, Array<string> suggestions>   (updated each data change)
//    uniquenes = Map<string, Set<string> alreadyExistingValues>  (updated each data change, used both for edit and display uniqueness)
//

interface SortingState {
  direction: number;
  columnIndex: number;
}

export function FastTable<T>(props: FastTableProps<T>) {
  const [sortingState, setSortingState] = useState<SortingState>({
    direction: 0,
    columnIndex: 0,
  });
  const [modifyTarget, setModifyTarget] = useState<T | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<T | undefined>(undefined);
  const [selectedRow, setSelectedRow] = useState<T | undefined>(undefined);
  const [highlightedRow, setHighlightedRow] = useState<T | undefined>(
    undefined
  );
  const dragSourceRef = useRef<T | null>(null);
  const listRef = useRef<any>(null);
  const rowHeightCacheRef = useRef<Map<number, number>>(new Map());
  const [sortedData, setSortedData] = useState<Array<DisplayDataRow<T>>>(
    mapDataToDisplayData(
      props,
      setModifyTarget,
      setDeleteTarget,
      selectedRow,
      setSelectedRow,
      dragSourceRef,
      highlightedRow
    )
  );
  const minRowHeight = 50;
  const estimatedRowHeight = (props.rowHeight ?? 50);


  useEffect(() => {
    setSortedData(
      mapDataToDisplayData(
        props,
        setModifyTarget,
        setDeleteTarget,
        selectedRow,
        setSelectedRow,
        dragSourceRef,
        highlightedRow
      )
    );
    //setSortingState({ direction: 0, columnIndex: 0 });
    setSortingState((old) => ({ ...old })); // Triggers next effect to sort the data
  }, [props.data, selectedRow, highlightedRow]);

  useEffect(() => {
    if (sortingState.direction === 0) {
      setSortedData(
        mapDataToDisplayData(
          props,
          setModifyTarget,
          setDeleteTarget,
          selectedRow,
          setSelectedRow,
          dragSourceRef,
          highlightedRow
        )
      );
    } else {
      setSortedData((oldData) => {
        return [
          ...oldData.sort(
            (a: DisplayDataRow<T>, b: DisplayDataRow<T>) =>
              sortingState.direction *
              compareValues(
                a.data[sortingState.columnIndex],
                b.data[sortingState.columnIndex]
              )
          ),
        ];
      });
    }
  }, [sortingState, selectedRow, highlightedRow]);

  useEffect(() => {
    if (props.focusedRow === undefined) {
      return;
    }

    setSelectedRow(props.focusedRow);
    setHighlightedRow(props.focusedRow);

    const focusedIndex = sortedData.findIndex(
      (row) => row.rawData === props.focusedRow
    );

    if (focusedIndex >= 0) {
      listRef.current?.scrollToItem(focusedIndex, "center");
    }

    const timeout = setTimeout(() => {
      setHighlightedRow((current) =>
        current === props.focusedRow ? undefined : current
      );
    }, 2500);

    return () => clearTimeout(timeout);
  }, [props.focusedRow, sortedData]);

  const getRowHeight = (index: number): number => {
    return rowHeightCacheRef.current.get(index) ?? estimatedRowHeight;
  };

  const setRowHeight = (index: number, height: number): void => {
    if (rowHeightCacheRef.current.get(index) !== height) {
      rowHeightCacheRef.current.set(index, height);
      listRef.current?.resetAfterIndex(index);
    }
  };

  const columnNames = new Array(
    props.headers.length + (props.onChange ? 1 : 0) + (props.onDelete ? 1 : 0)
  );
  for (let i = 0; i < props.headers.length; i++) {
    columnNames[i] = props.headers[i];
  }

  return (
    <VStack width={props.width ?? "100%"}>
      <Box
        width="100%"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      >
        <TableHeader
          columnNames={columnNames}
          tamplateColumns={props.rowFormat}
          sortingState={sortingState}
          setSortingState={setSortingState}
        />
        <List
          ref={listRef}
          height={props.height ?? 500} // Set the height of the list
          itemCount={sortedData.length}
          itemSize={getRowHeight} // Dynamic row height function
          width="100%"
          itemData={{
            rows: sortedData,
            setRowHeight: setRowHeight,
          }}
        >
          {Row<T>}
        </List>
        {modifyTarget && (
          <ModifyModal
            target={modifyTarget}
            editableFields={props.modifiableFields || []}
            fixedFields={props.fixedFields || []}
            displayEditableNames={props.modifiableFieldsDisplayName || []}
            displayFixedNames={props.fixedFieldsDisplayName || []}
            onClose={() => {
              setModifyTarget(undefined);
            }}
            onSubmit={async (updated, old) => {
              const success = await props.onChange!(updated, old);
              if (success) {
                setDeleteTarget(undefined);
                return true;
              }
              return false;
            }}
            forceDisplayToTypeMap={props.forceDisplayToTypeMap}
          />
        )}
        {deleteTarget && (
          <DeleteModal
            onAbort={() => {
              setDeleteTarget(undefined);
            }}
            onDelete={() => {
              props.onDelete!(deleteTarget).then((success: boolean) => {
                if (success) {
                  setDeleteTarget(undefined);
                }
              });
            }}
          />
        )}
      </Box>
      <ExcelDownloader
        data={{
          headers: sortedData[0]?.headers ?? [],
          rows: sortedData.map((r) => r.data),
        }}
        suggestedFilename={props.downloadFileName ?? ""}
        columnWidths={props.columnWidths}
        pageHeader={props.pageHeader}
        repeatHeaderRows={props.repeatHeaderRows}
        hide={false}
      />
    </VStack>
  );
}

function ExcelDownloader(props: {
  data: any[] | { headers: Array<string>; rows: Array<Array<any>> }; // Array of any json representation of row OR header and rows format
  suggestedFilename: string;
  columnWidths?: number[];
  pageHeader?: string;
  repeatHeaderRows?: number;
  hide?: boolean;
}) {
  const [showModal, setShowModal] = useState(false);

  if (props.hide) {
    return null;
  }

  return (
    <Stack width={"100%"}>
      {showModal && (
        <PromptForInputModal
          onAbort={() => setShowModal(false)}
          onConfirm={async (fileName) => {
            if (Array.isArray(props.data)) {
              await downloadExcel(props.data, fileName, props.columnWidths, props.pageHeader, props.repeatHeaderRows);
            } else {
              const headers = props.data.headers;
              const rows = props.data.rows;

              const zipped = rows.map((row) =>
                headers.reduce((acc, key, i) => {
                  acc[key] = row[i];
                  return acc;
                }, {} as Record<string, any>)
              );

              await downloadExcel(zipped, fileName, props.columnWidths, props.pageHeader, props.repeatHeaderRows);
            }
            setShowModal(false);
          }}
          request="Välj filnamn"
          suggestedInput={props.suggestedFilename}
          abortButtonTitle="Avbryt"
          confirmButtonTitle="OK"
          colorScheme="green"
        />
      )}
      <Button
        width={"100%"}
        onClick={() => {
          setShowModal(true);
        }}
      >
        Ladda ner som excel fil
      </Button>
    </Stack>
  );
}

// Table header component
const TableHeader = (props: {
  columnNames: Array<string | undefined>;
  tamplateColumns: string;
  setSortingState: React.Dispatch<React.SetStateAction<SortingState>>;
  sortingState: SortingState;
}) => (
  <Grid
    templateColumns={props.tamplateColumns /*"1fr 1fr 8fr 8fr 1fr 1fr 1fr"*/}
    padding="4"
    fontWeight="bold"
    bg="gray.100"
    width={"100%"}
  >
    {props.columnNames.map((cn, index) => (
      <GridItem
        width={"100%"}
        _hover={{ color: "green" }}
        key={"table_header_" + cn + "-" + index}
        cursor={"pointer"}
        onClick={() => {
          props.setSortingState((oldState: SortingState) => {
            const newState = {
              ...oldState,
              columnIndex: index,
            } as SortingState;
            if (oldState.columnIndex === newState.columnIndex) {
              newState.direction =
                oldState.direction === 1
                  ? -1
                  : oldState.direction === -1
                  ? 0
                  : 1;
            } else {
              newState.direction = 1;
            }
            return newState;
          });
        }}
      >
        <HStack>
          <Text>{cn}</Text>
          {index === props.sortingState.columnIndex && (
            <Icon>
              {props.sortingState.direction === -1 && <ArrowDown />}
              {props.sortingState.direction === 1 && <ArrowUp />}
            </Icon>
          )}
        </HStack>
      </GridItem>
    ))}
  </Grid>
);

// Row component to render each participant
function Row<T>({
  index,
  style,
  data,
}: ListChildComponentProps<any>) {
  const rowsData = data.rows as DisplayDataRow<T>[];
  const setRowHeight = data.setRowHeight as (index: number, height: number) => void;
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gridRef.current) return;

    let timeoutId: NodeJS.Timeout;
    
    // Use ResizeObserver to detect actual content height changes
    // Use scrollHeight to measure natural content height (not constrained by style.height from react-window)
    const resizeObserver = new ResizeObserver(() => {
      // Debounce to avoid excessive updates
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (gridRef.current) {
          // scrollHeight gives the natural content height
          // Constrain between 50px and 300px to avoid extremely large rows
          const naturalHeight = gridRef.current.scrollHeight;
          const height = Math.max(50, Math.min(naturalHeight, 300));
          setRowHeight(index, height);
        }
      }, 50);
    });

    resizeObserver.observe(gridRef.current);
    
    // Trigger initial measurement after a brief delay to ensure content is laid out
    const initialTimer = setTimeout(() => {
      if (gridRef.current) {
        const naturalHeight = gridRef.current.scrollHeight;
        const height = Math.max(50, Math.min(naturalHeight, 300));
        setRowHeight(index, height);
      }
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
    };
  }, [index, setRowHeight]);

  const headers = rowsData[index].headers;
  const row = rowsData[index].data;
  const rawDataRow = rowsData[index].rawData;
  const rowFormat = rowsData[index].rowFormat;
  const uniqueness = rowsData[index].uniqueness;
  const onChange = rowsData[index].onChange;
  const onDelete = rowsData[index].onDelete;
  const setModifyTarget = rowsData[index].setModifyTarget;
  const setDeleteTarget = rowsData[index].setDeleteTarget;
  const isSelected = rowsData[index].selectedRow === rawDataRow;
  const isHighlighted = rowsData[index].highlightedRow === rawDataRow;

  return (
    <Grid
      ref={gridRef}
      templateColumns={rowFormat /*"1fr 1fr 8fr 8fr 1fr 1fr 1fr"*/}
      style={style}
      padding="4"
      bg={
        isHighlighted
          ? "yellow.200"
          : isSelected
          ? "blue.100"
          : index % 2 === 0
          ? "white"
          : "gray.50"
      }
      alignItems="start"
      borderBottom="1px solid"
      borderColor={isHighlighted ? "yellow.400" : "gray.200"}
      cursor={rowsData[index].onRowSelect ? "pointer" : "default"}
      onClick={() => {
        if (rowsData[index].onRowSelect) {
          const already = rowsData[index].selectedRow === rawDataRow;
          const newSelection = already ? undefined : rawDataRow;
          rowsData[index].setSelectedRow?.(newSelection);
          rowsData[index].onRowSelect?.(newSelection);
        }
      }}
      _hover={rowsData[index].onRowSelect ? { bg: "blue.50" } : {}}
      draggable={!!rowsData[index].onReorder}
      onDragStart={rowsData[index].onReorder ? (e) => {
        rowsData[index].dragSourceRef!.current = rawDataRow;
        e.dataTransfer.effectAllowed = "move";
      } : undefined}
      onDragOver={rowsData[index].onReorder ? (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      } : undefined}
      onDrop={rowsData[index].onReorder ? (e) => {
        e.preventDefault();
        const source = rowsData[index].dragSourceRef?.current;
        if (source && source !== rawDataRow) {
          rowsData[index].onReorder!(source, rawDataRow);
          rowsData[index].dragSourceRef!.current = null;
        }
      } : undefined}
    >
      {row.map((cellData, columnIndex) => {
        const headerName = headers[columnIndex];
        const existingValuesThatShouldBeUnique = uniqueness?.has(headerName)
          ? uniqueness?.get(headerName)
          : new Array();
        const failedUniquenessCheck =
          (existingValuesThatShouldBeUnique?.length ?? 0) > 0 &&
          (existingValuesThatShouldBeUnique?.filter((v) => v === cellData)
            .length ?? 0) > 1;
        if (isBoolean(cellData)) {
          return (
            <GridItem
              width={"100%"}
              key={`fastFakeTableRow${index}:${columnIndex}`}
              cursor={rowsData[index].onRowSelect ? "pointer" : "default"}
              onClick={() => {
                if (rowsData[index].onRowSelect) {
                  rowsData[index].setSelectedRow?.(rawDataRow);
                  rowsData[index].onRowSelect?.(rawDataRow);
                }
              }}
            >
              <Checkbox isChecked={cellData} />
            </GridItem>
          );
        } else if (isDate(cellData)) {
          return (
            <GridItem
              width={"100%"}
              key={`fastFakeTableRow${index}:${columnIndex}`}
              cursor={rowsData[index].onRowSelect ? "pointer" : "default"}
              onClick={() => {
                if (rowsData[index].onRowSelect) {
                  rowsData[index].setSelectedRow?.(rawDataRow);
                  rowsData[index].onRowSelect?.(rawDataRow);
                }
              }}
            >
              <DatePicker
                selected={new Date(cellData)}
                // onChange={(date) => {
                //   const newRow = { ...rawDataRow };
                //   newRow[headers[columnIndex] as keyof T] = date;
                //   setModifyTarget(newRow);
                // }}
                dateFormat="yyyy-MM-dd HH:mm:ss"
                showTimeSelect
                timeFormat="HH:mm:ss"
                timeIntervals={5}
                timeCaption="Tid"
                locale="sv"
              />
            </GridItem>
          );
        } else {
          return (
            <GridItem
              key={`fastFakeTableRow${index}:${columnIndex}`}
              width={"100%"}
              cursor={rowsData[index].onRowSelect ? "pointer" : "default"}
              onClick={() => {
                if (rowsData[index].onRowSelect) {
                  rowsData[index].setSelectedRow?.(rawDataRow);
                  rowsData[index].onRowSelect?.(rawDataRow);
                }
              }}
            >
              <Text
                textColor={failedUniquenessCheck ? "red" : "black"}
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="break-word"
              >{`${cellData}`}</Text>
            </GridItem>
          );
        }
      })}
      {onChange && (
        <GridItem width={"100%"}>
          <IconButton
            aria-label="Ändra"
            size={"20px"}
            variant={"ghost"}
            onClick={() => {
              setModifyTarget(rawDataRow);
            }}
          >
            <WrenchIcon />
          </IconButton>
        </GridItem>
      )}
      {onDelete && (
        <GridItem width={"100%"}>
          <IconButton
            aria-label="Ändra"
            size={"20px"}
            variant={"ghost"}
            onClick={() => {
              setDeleteTarget(rawDataRow);
            }}
          >
            <TrashIcon />
          </IconButton>
        </GridItem>
      )}
    </Grid>
  );
}
export function isNumber(value: any): value is number {
  return typeof value === "number" || `${parseFloat(value)}` == value;
}
export function isBoolean(value: any): value is boolean {
  return typeof value === "boolean";
}

export function isDate(value: any): value is Date {
  return value instanceof Date;
}

function compareValues(
  a: string | boolean | number | undefined | null,
  b: string | boolean | number | undefined | null
) {
  if (a == null) {
    return b == null ? 0 : -1;
  } else if (b == null) {
    return 1;
  } else if (isNumber(a) && isNumber(b)) {
    return (a as number) - (b as number);
  } else if (isBoolean(a) && isBoolean(b)) {
    return (a ? 1 : -1) - (b ? 1 : -1);
  } else {
    return String(a).localeCompare(String(b));
  }
}

function mapDataToDisplayData<T>(
  props: FastTableProps<T>,
  setModifyTarget: React.Dispatch<React.SetStateAction<T | undefined>>,
  setDeleteTarget: React.Dispatch<React.SetStateAction<T | undefined>>,
  selectedRow: T | undefined,
  setSelectedRow: React.Dispatch<React.SetStateAction<T | undefined>>,
  dragSourceRef: MutableRefObject<T | null>,
  highlightedRow: T | undefined
): Array<DisplayDataRow<T>> {
  return props.data.map((d) => ({
    data: props.toRowData(d),
    headers: props.headers,
    rawData: d,
    rowFormat: props.rowFormat,
    setModifyTarget: setModifyTarget,
    setDeleteTarget: setDeleteTarget,
    modifiableFields: props.modifiableFields,
    modifiableFieldsDisplayName: props.modifiableFieldsDisplayName,
    onChange: props.onChange,
    onDelete: props.onDelete,
    suggestions: props.suggestions,
    uniqueness: props.uniqueness,
    selectedRow: selectedRow,
    setSelectedRow: setSelectedRow,
    onRowSelect: props.onRowSelect,
    onReorder: props.onReorder,
    dragSourceRef: dragSourceRef,
    highlightedRow: highlightedRow,
  })) as Array<DisplayDataRow<T>>;
}
